<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    dialog-width="100%"
    body-height="80vh"
    max-height="95vh"
    no-paddings-y
    no-paddings-x
    header-shadow
    :title="$i18n.t('sidebar.locationAnalysis.locationAnalysisResult')"
    agree-text="dialog.downloadTable"
  >
    <template #body>
      <main-loader class="pt-10" v-if="!analysis?.data" />
      <template v-else>
        <v-row no-gutters class="fill-height">
          <v-col cols="6" class="px-7 py-6 right-border fill-height" fill-height style="overflow-x: auto">
            <card-title-row translationPath="sidebar.locationAnalysis.travelTime" :has-icon-column="false" />
            <v-row no-gutters class="pt-4">
              <v-col cols="12">
                <v-data-table
                  :headers="timeTableHeaders"
                  height="auto"
                  class="locationAnalysisResultTable"
                  hide-default-footer
                  disable-pagination
                  fixed-header
                  :items="analysis.data"
                >
                  <template
                    v-for="(header, idx) of currentAnalysisTransportTypes"
                    #[`header.data.${header.name}_seconds`]
                  >
                    <span :key="idx">
                      <v-icon size="22" color="rgba(0,0,0,0.7)"> {{ header.icon }} </v-icon>
                    </span>
                  </template>
                  <template
                    v-for="(header, idx) of currentAnalysisTransportTypes"
                    #[`item.data.${header.name}_seconds`]="{ item }"
                  >
                    <span :key="idx">
                      {{ parseInt(item.data[`${header.name}_seconds`] / 60) + 1 }}
                    </span>
                  </template>
                  <template #item.index="a">
                    {{ a.index + 1 }}
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="6" class="px-7 py-6 fill-height" style="overflow-x: auto">
            <card-title-row translationPath="sidebar.locationAnalysis.reception" :has-icon-column="false" />
            <v-row no-gutters class="pt-4">
              <v-col cols="12">
                <canvas id="chart" role="img" height="500px" width="500px"></canvas>
              </v-col>
            </v-row>
            <v-row no-gutters class="pt-7">
              <v-col cols="6">
                <v-row no-gutters v-for="(serie, idx) of chartSeries" :key="idx" align="center">
                  <v-col cols="auto">
                    <data-input
                      v-model="serie.color"
                      v-bind="colorInput"
                      @saveDate="updateChartSerie(idx, 'color', $event)"
                    />
                  </v-col>
                  <v-col cols="4" class="pl-2 pr-1">
                    <data-input
                      v-model="serie.min"
                      v-bind="{ ...chartFilterInput, label: $_capitalize($i18n.t('default.from')) }"
                      @input="updateChartSerie(idx, 'min', $event)"
                    />
                  </v-col>
                  <v-col cols="4" class="pr-2 pl-1">
                    <data-input
                      v-model="serie.max"
                      v-bind="{ ...chartFilterInput, label: $_capitalize($i18n.t('default.to')) }"
                      @input="updateChartSerie(idx, 'max', $event)"
                    />
                  </v-col>
                  <v-col cols="auto" class="pl-3">
                    <v-icon size="20" @click="deleteChartSerie(idx)">mdi-close</v-icon>
                  </v-col>
                </v-row>
                <v-form ref="newSerieForm">
                  <v-row no-gutters align="center">
                    <v-col cols="auto">
                      <data-input v-model="newSerie.color" v-bind="colorInput" />
                    </v-col>
                    <v-col cols="4" class="pl-2 pr-1">
                      <data-input
                        v-model="newSerie.min"
                        v-bind="{ ...chartFilterInput, label: $_capitalize($i18n.t('default.from')) }"
                      />
                    </v-col>
                    <v-col cols="4" class="pr-2 pl-1">
                      <data-input
                        v-model="newSerie.max"
                        v-bind="{ ...chartFilterInput, label: $_capitalize($i18n.t('default.to')) }"
                      />
                    </v-col>
                    <v-col cols="auto">
                      <base-rounded-button
                        color="success"
                        icon="mdi-plus"
                        size="x-small"
                        @click="addChartSerie()"
                        :disabled="
                          !newSerie.color ||
                            (!newSerie.min && newSerie.min != 0) ||
                            (!newSerie.max && newSerie.max != 0)
                        "
                      />
                    </v-col>
                  </v-row>
                </v-form>
              </v-col>
              <v-col cols="6">
                <v-data-table
                  :headers="rangeTableHeaders"
                  height="auto"
                  class="locationAnalysisResultTable"
                  hide-default-footer
                  disable-pagination
                  fixed-header
                  :items="rangeTableData"
                >
                  <template
                    v-for="(header, idx) of currentAnalysisTransportTypes"
                    #[`header.count${$_capitalize(header.name)}`]
                  >
                    <span :key="idx">
                      <v-icon size="22" color="rgba(0,0,0,0.7)"> {{ header.icon }} </v-icon>
                    </span>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </template>
    </template>
    <template #buttons>
      <v-spacer></v-spacer>
      <v-col cols="auto">
        <button-main-dialog cancel @click="close" :text="$i18n.t('dialog.disagree')" />
      </v-col>
      <v-col cols="auto">
        <button-main-dialog @click="getChart()" :text="$i18n.t('dialog.downloadChart')" />
      </v-col>
      <v-col cols="auto">
        <button-main-dialog @click="getCsv()" :text="$i18n.t('dialog.downloadTable')" />
      </v-col>
    </template>
  </main-large-dialog>
</template>
<script>
import { get as lodashGet } from 'lodash';

import ButtonMainDialog from '@/components/ButtonMainDialog';
import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader';
import MainLargeDialog from '@/components/MainLargeDialog';

import Chart from 'chart.js';
import { asArray, asString } from 'ol/color';

export default {
  name: 'LocationAnalysisResult',
  components: { ButtonMainDialog, CardTitleRow, DataInput, MainLargeDialog, MainLoader },
  props: {
    analysis: {
      type: Object,
    },
    isVisible: {
      type: Boolean,
      default: false,
    },
    transportTypes: {
      type: Array,
      default: () => {
        return [];
      },
    },
  },
  computed: {
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    colorInput() {
      return {
        dataType: { name: 'color' },
      };
    },
    chartFilterInput() {
      return {
        dataType: { name: 'integer' },
        hideDetails: true,
        allowNegative: false,
        hideArrows: true,
        rules: [v => !!v || v === 0 || ''],
      };
    },
    timeTableHeaders() {
      const defaultProps = {
        sortable: false,
        align: 'center',
      };
      return [
        {
          text: this.$i18n.t('default.lp'),
          csvText: this.$i18n.t('default.lp'),
          value: 'index',
          ...defaultProps,
        },
        {
          text: this.$i18n.t('default.name'),
          csvText: this.$i18n.t('default.name'),
          value: 'name',
          ...defaultProps,
        },
        ...this.currentAnalysisTransportTypes.map(type => {
          return {
            text: type.text,
            csvText: type.text,
            value: `data.${type.name}_seconds`,
            ...defaultProps,
          };
        }),
      ];
    },
    rangeTableHeaders() {
      const defaultProps = {
        sortable: false,
        align: 'center',
      };
      return [
        {
          text: `${this.$i18n.t('default.time')} [min]`,
          csvText: `${this.$i18n.t('default.time')} [min]`,
          value: 'timeRange',
          ...defaultProps,
        },
        ...this.currentAnalysisTransportTypes.map(type => {
          return {
            text: type.text,
            csvText: type.text,
            value: `count${this.$_capitalize(type.name)}`,
            filterKey: type.name,
            ...defaultProps,
          };
        }),
      ];
    },
    currentAnalysisTransportTypes() {
      if (!this.analysis.transportTypes) return this.transportTypes;
      return this.transportTypes.filter(type => this.analysis.transportTypes.includes(type.name));
    },
  },
  data: () => ({
    newSerie: { color: '#ffffff' },
    chartSeries: [],
    rangeTableData: [],
    isChartReady: false,
  }),
  methods: {
    initRangeTableData() {
      const rangeTableData = this.chartSeries.map(serie => {
        return {
          min: serie.min,
          max: serie.max,
          timeRange: `${serie.min === null ? '?' : serie.min}-${serie.max === null ? '?' : serie.max}`,
        };
      });
      this.analysis.data?.forEach(item => {
        const { count } = item;
        rangeTableData.forEach(range => {
          this.currentAnalysisTransportTypes.forEach(type => {
            if (!range[`count${this.$_capitalize(type.name)}`]) range[`count${this.$_capitalize(type.name)}`] = 0;
            if (
              item.data[`${type.name}_seconds`] / 60 >= range.min &&
              item.data[`${type.name}_seconds`] / 60 <= range.max
            ) {
              range[`count${this.$_capitalize(type.name)}`] += count;
            }
          });
        });
      });
      this.rangeTableData = rangeTableData;
    },
    getRangeTableElementData(serie) {
      const range = {
        min: serie.min,
        max: serie.max,
        timeRange: `${serie.min === null ? '?' : serie.min}-${serie.max === null ? '?' : serie.max}`,
      };
      this.analysis.data?.forEach(item => {
        const { count } = item;
        this.currentAnalysisTransportTypes.forEach(type => {
          if (!range[`count${this.$_capitalize(type.name)}`]) range[`count${this.$_capitalize(type.name)}`] = 0;
          if (
            item.data[`${type.name}_seconds`] / 60 >= range.min &&
            item.data[`${type.name}_seconds`] / 60 <= range.max
          ) {
            range[`count${this.$_capitalize(type.name)}`] += count;
          }
        });
      });
      return range;
    },
    initChart() {
      if (this.isChartReady) this.chart.destroy();
      this.isChartReady = false;
      const canvas = document.getElementById('chart');
      if (!canvas) return;
      Chart.plugins.register({
        afterRender: function(c) {
          const ctx = c.chart.ctx;
          ctx.save();
          ctx.globalCompositeOperation = 'destination-over';
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, c.chart.width, c.chart.height);
          ctx.restore();
        },
      });
      Chart.Legend.prototype.afterFit = function() {
        this.height = this.height + 20;
      };
      this.chart = new Chart(canvas, {
        options: {
          responsive: true,
          maintainAspectRatio: false,
          tooltips: {
            callbacks: {
              label: tooltipItems => {
                return tooltipItems?.value || 0;
              },
              title: tooltipItems => {
                const titles = [];
                tooltipItems.forEach(item => {
                  if (!titles.includes(this.transportTypes[item.index]?.text)) {
                    titles.push(this.transportTypes[item.index]?.text);
                  }
                });
                return titles;
              },
            },
          },
        },
        type: 'radar',
        data: {
          labels: this.transportTypes.map(type => type.text),
          datasets: this.getDefaultDatasets(),
        },
      });
      this.chart.update();
      this.isChartReady = true;
    },
    getDefaultDatasets() {
      return this.chartSeries.map(serie => this.getChartDataset(serie));
    },
    getChartDataset(serie) {
      const hexToRgba = (hex, opacity) => {
        const [r, g, b] = Array.from(asArray(hex));
        return asString([r, g, b, opacity]);
      };
      const counts = this.transportTypes.reduce((total, current) => {
        total[current.name] = 0;
        return total;
      }, {});
      this.analysis.data.forEach(item => {
        this.transportTypes.forEach(type => {
          if (
            item.data[`${type.name}_seconds`] / 60 >= serie.min &&
            item.data[`${type.name}_seconds`] / 60 <= serie.max
          ) {
            counts[type.name] += item.count;
          }
        });
      });
      const label =
        serie.min === 0
          ? `< ${serie.max === null ? '?' : serie.max} min`
          : `${serie.min === null ? '?' : serie.min} - ${serie.max === null ? '?' : serie.max} min`;
      return {
        label,
        data: Object.values(counts),
        fill: true,
        backgroundColor: hexToRgba(serie.color, 0.2),
        borderColor: hexToRgba(serie.color, 1),
        pointBackgroundColor: hexToRgba(serie.color, 1),
        pointBorderColor: 'rgba(0,0,0,0.5)',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: hexToRgba(serie.color, 1),
      };
    },
    addChartSerie(serie = this.newSerie) {
      this.chartSeries.push({ ...serie });
      this.newSerie = { color: '#ffffff' };

      const newRangeTableElementData = this.getRangeTableElementData(serie);
      this.rangeTableData.push(newRangeTableElementData);

      const newDataset = this.getChartDataset(serie);
      this.chart.data.datasets.push(newDataset);
      this.chart.update();
      this.$refs.newSerieForm?.resetValidation();
    },
    updateChartSerie(idx, key, value) {
      const updateRangeTableElementData = this.getRangeTableElementData(
        JSON.parse(JSON.stringify({ ...this.chartSeries[idx], [key]: value }))
      );
      this.$set(this.rangeTableData, idx, updateRangeTableElementData);

      const updateDataset = this.getChartDataset(
        JSON.parse(JSON.stringify({ ...this.chartSeries[idx], [key]: value }))
      );
      const dataset = this.chart.data.datasets[idx];
      if (dataset) Object.assign(dataset, updateDataset);
      this.chart.update();
    },
    deleteChartSerie(idx) {
      this.chartSeries.splice(idx, 1);
      this.rangeTableData.splice(idx, 1);
      this.chart.data.datasets.splice(idx, 1);
      this.chart.update();
    },
    close() {
      this.computedIsVisible = false;
      this.$emit('modalClosed');
    },
    getCsv(name = this.analysis?.name) {
      const timeTableContent = `${this.timeTableHeaders.map(header => header.csvText).join(';')}\n${this.analysis?.data
        .map((item, index) =>
          this.timeTableHeaders
            .map(header => {
              const v = lodashGet(item, header.value);
              if (header.value.includes('seconds')) return parseInt(v / 60) + 1;
              else if (header.value === 'index') return index + 1;
              return v;
            })
            .join(';')
        )
        .join('\n')}`;
      const rangeTableContent = `${this.rangeTableHeaders
        .map(header => header.csvText)
        .join(';')}\n${this.rangeTableData
        .map(item =>
          this.rangeTableHeaders
            .map(header => {
              const v = lodashGet(item, header.value);
              return v;
            })
            .join(';')
        )
        .join('\n')}`;
      const a = `${timeTableContent}\n\n${rangeTableContent}`;
      const file = new Blob([a], { type: 'text/csv;charset=utf-8;' });
      this.$_saveFile(URL.createObjectURL(file), `${name ? name : this.$i18n.t('dialog.body.table')}.csv`);
    },
    getChart(name = this.analysis?.name) {
      this.$_saveFile(this.chart.toBase64Image(), name ? name : this.$i18n.t('dialog.body.chart'));
    },
  },
  watch: {
    chartSeries: {
      handler(nV) {
        localStorage.setItem('location-analysis-chart-series', JSON.stringify(nV || []));
      },
      deep: true,
    },
  },
  mounted() {
    this.chartSeries = JSON.parse(localStorage.getItem('location-analysis-chart-series')) || [];
    this.$nextTick(() => {
      this.initChart();
      this.initRangeTableData();
    });
  },
};
</script>
<style lang="scss" scoped>
.locationAnalysisResultTable {
  @include border();
}
.top-border {
  @include border(top, 1px);
}
.right-border {
  @include border(right, 1px);
}
.sticky-footer-table {
  position: sticky;
  bottom: 0;
  background-color: #fff;
  z-index: 1;
}
.activeTab {
  background-color: rgba(207, 207, 207, 0.25);
}
::v-deep {
  .location-analysis-table-input {
    .v-input__slot {
      min-height: 32px !important;
    }
    .v-input__append-inner {
      margin-top: 4px !important;
    }
    .v-label {
      font-size: 16px;
      top: 7px !important;
    }
  }
  .v-slide-group {
    z-index: 99;
    position: sticky;
    top: 0;
    box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14),
      0px 1px 10px 0px rgba(0, 0, 0, 0.12) !important;
  }
}
</style>
